import { STATION_TYPES } from '../../data/domain';
import type { SnapshotFrame, Station } from '../../data/types';

/**
 * A site snapshot is a fixed 6 × 5 grid of zoom-18 satellite tiles (about 230 m × 190 m in North Queensland)
 * centred on the framed map view. Stations are stored as latitude/longitude, so they stay correct regardless
 * of how the snapshot is zoomed or panned on screen.
 */
export const SNAPSHOT_ZOOM = 18;
export const TILES_WIDE = 6;
export const TILES_HIGH = 5;
export const TILE_SIZE = 256;
export const MIN_SCALE = 1;
export const MAX_SCALE = 6;

const worldSize = 2 ** SNAPSHOT_ZOOM * TILE_SIZE;

export function latLngToPixel(lat: number, lng: number) {
  return { x: ((lng + 180) / 360) * worldSize, y: ((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * worldSize };
}

export function pixelToLatLng(x: number, y: number) {
  return { lat: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / worldSize))) * 180) / Math.PI, lng: (x / worldSize) * 360 - 180 };
}

export function frameAround(centre: { lat: number; lng: number }): SnapshotFrame {
  const pixel = latLngToPixel(centre.lat, centre.lng);
  return { originX: Math.floor(pixel.x / TILE_SIZE) - 3, originY: Math.floor(pixel.y / TILE_SIZE) - 2, crop: { scale: 1, x: 0, y: 0 } };
}

export function tileUrl(layer: string, x: number, y: number) {
  const tiles = 2 ** SNAPSHOT_ZOOM;
  return `https://server.arcgisonline.com/ArcGIS/rest/services/${layer}/MapServer/tile/${SNAPSHOT_ZOOM}/${y}/${((x % tiles) + tiles) % tiles}`;
}

export const SNAPSHOT_LAYERS = ['World_Imagery', 'Reference/World_Transportation', 'Reference/World_Boundaries_and_Places'];

/** Screen position of a station inside a snapshot element of the given size. */
export function stationPosition(station: Station, frame: SnapshotFrame, size: { width: number; height: number }) {
  const pixel = latLngToPixel(station.lat, station.lng);
  const sceneX = ((pixel.x - frame.originX * TILE_SIZE) / (TILES_WIDE * TILE_SIZE)) * size.width;
  const sceneY = ((pixel.y - frame.originY * TILE_SIZE) / (TILES_HIGH * TILE_SIZE)) * size.height;
  return { x: frame.crop.x + sceneX * frame.crop.scale, y: frame.crop.y + sceneY * frame.crop.scale };
}

/** Latitude/longitude under a point on screen, or null when the point is outside the snapshot. */
export function pointToLatLng(offsetX: number, offsetY: number, frame: SnapshotFrame, size: { width: number; height: number }) {
  const x = (offsetX - frame.crop.x) / frame.crop.scale;
  const y = (offsetY - frame.crop.y) / frame.crop.scale;
  if (x < 0 || y < 0 || x > size.width || y > size.height) return null;
  return pixelToLatLng(frame.originX * TILE_SIZE + (x / size.width) * TILES_WIDE * TILE_SIZE, frame.originY * TILE_SIZE + (y / size.height) * TILES_HIGH * TILE_SIZE);
}

/** Zooms around a point so the spot under the finger or cursor stays put. */
export function zoomFrame(frame: SnapshotFrame, nextScale: number, focus: { x: number; y: number }): SnapshotFrame {
  const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextScale));
  if (scale === frame.crop.scale) return frame;
  const sceneX = (focus.x - frame.crop.x) / frame.crop.scale;
  const sceneY = (focus.y - frame.crop.y) / frame.crop.scale;
  return { ...frame, crop: { scale, x: focus.x - sceneX * scale, y: focus.y - sceneY * scale } };
}

export function sceneBounds(frame: SnapshotFrame) {
  const topLeft = pixelToLatLng(frame.originX * TILE_SIZE, frame.originY * TILE_SIZE);
  const bottomRight = pixelToLatLng((frame.originX + TILES_WIDE) * TILE_SIZE, (frame.originY + TILES_HIGH) * TILE_SIZE);
  return { west: topLeft.lng, north: topLeft.lat, east: bottomRight.lng, south: bottomRight.lat };
}

function drawLegend(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, scale: number) {
  const entries = Object.values(STATION_TYPES);
  const pad = 12 * scale; const row = 19 * scale; const width = 178 * scale;
  const height = pad * 2 + 18 * scale + entries.length * row;
  const x = 14 * scale; const y = canvas.height - height - 14 * scale;
  context.fillStyle = 'rgba(255,255,255,.92)'; context.strokeStyle = 'rgba(35,35,32,.55)'; context.lineWidth = scale;
  context.fillRect(x, y, width, height); context.strokeRect(x, y, width, height);
  context.fillStyle = '#262724'; context.font = `700 ${10 * scale}px Inter, Arial`; context.textAlign = 'left'; context.textBaseline = 'middle';
  context.fillText('STATION LEGEND', x + pad, y + pad + 5 * scale);
  entries.forEach((type, index) => {
    const cy = y + pad + 18 * scale + index * row + row / 2;
    context.beginPath(); context.arc(x + pad + 6 * scale, cy, 5 * scale, 0, Math.PI * 2); context.fillStyle = type.color; context.fill();
    context.lineWidth = scale; context.strokeStyle = '#fff'; context.stroke();
    context.fillStyle = '#262724'; context.font = `600 ${9 * scale}px Inter, Arial`; context.fillText(type.label, x + pad + 18 * scale, cy);
  });
}

function drawCompass(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, scale: number) {
  const radius = 19 * scale; const x = canvas.width - 38 * scale; const y = 42 * scale;
  context.fillStyle = 'rgba(255,255,255,.92)'; context.strokeStyle = 'rgba(35,35,32,.7)'; context.lineWidth = scale;
  context.beginPath(); context.arc(x, y, radius, 0, Math.PI * 2); context.fill(); context.stroke();
  context.beginPath(); context.moveTo(x, y - radius + 4 * scale); context.lineTo(x - 6 * scale, y + 7 * scale); context.lineTo(x, y + 3 * scale); context.lineTo(x + 6 * scale, y + 7 * scale); context.closePath(); context.fillStyle = '#262724'; context.fill();
  context.beginPath(); context.moveTo(x, y + radius - 4 * scale); context.lineTo(x - 6 * scale, y - 7 * scale); context.lineTo(x, y - 3 * scale); context.lineTo(x + 6 * scale, y - 7 * scale); context.closePath(); context.fillStyle = '#fff'; context.fill();
  context.fillStyle = '#262724'; context.font = `800 ${10 * scale}px Inter, Arial`; context.textAlign = 'center'; context.textBaseline = 'middle';
  context.fillText('N', x, y - radius - 8 * scale);
}

/** Renders the snapshot exactly as shown (crop, stations, legend, north arrow) to a 2× PNG. */
export function exportSnapshotPng(element: HTMLElement, frame: SnapshotFrame, stations: Station[]): Promise<Blob> {
  const rect = element.getBoundingClientRect();
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(rect.width * scale); canvas.height = Math.round(rect.height * scale);
  const context = canvas.getContext('2d')!;
  const { crop } = frame;
  context.save();
  context.translate(crop.x * scale, crop.y * scale);
  context.scale(crop.scale * scale, crop.scale * scale);
  element.querySelectorAll<HTMLImageElement>('.snapshot-tile').forEach((image) => {
    if (!image.complete || !image.naturalWidth) throw new Error('Snapshot tiles are still loading.');
    const col = Number(image.dataset.col); const row = Number(image.dataset.row);
    context.drawImage(image, (col * rect.width) / TILES_WIDE, (row * rect.height) / TILES_HIGH, rect.width / TILES_WIDE, rect.height / TILES_HIGH);
  });
  context.restore();
  stations.forEach((station, index) => {
    const position = stationPosition(station, frame, rect);
    const x = position.x * scale; const y = position.y * scale;
    context.beginPath(); context.arc(x, y, 14 * scale, 0, Math.PI * 2); context.fillStyle = STATION_TYPES[station.type].color; context.fill();
    context.lineWidth = 2 * scale; context.strokeStyle = '#ffffff'; context.stroke();
    context.fillStyle = station.type === 'fgar' ? '#292715' : '#ffffff'; context.font = `700 ${11 * scale}px Inter, Arial`; context.textAlign = 'center'; context.textBaseline = 'middle';
    context.fillText(String(index + 1), x, y);
  });
  drawLegend(context, canvas, scale);
  drawCompass(context, canvas, scale);
  return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG export failed.'))), 'image/png'));
}
